"use client";

import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css"; // Import Bootstrap CSS

const Help: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [language, setLanguage] = useState<"EN" | "ZU">("EN"); // Language state: "EN" for English, "ZU" for Zulu
  const [videoContent, setVideoContent] = useState<any[]>([]);

  const faqs = {
    EN: [
      {
        question: "How long does the OTP take to be sent?", 
        answer:
          "The OTP (One-Time Pin) is usually sent within a few seconds. However, in rare cases, it can take up to 2 minutes. If you do not receive the OTP, ensure your mobile number is correct and check your spam or blocked messages folder.",
      },
      {
        question: "What should I do if I didn't receive my OTP?",
        answer:
          "If you didn't receive your OTP, try the following steps: (1) Ensure you have good network coverage. (2) Click on 'Resend OTP' on the page. (3) If the issue persists, contact our support team for assistance.",
      },
      {
        question: "Why is it not redirecting to the payment page?",
        answer:
          "If the payment page is not loading, ensure your internet connection is stable. Clear your browser cache and try again. If the issue persists, try using a different browser or device and contact support if needed.",
      },
      {
        question: "Why is the page freezing or not responding?",
        answer:
          "A freezing page could be caused by a slow device or unstable internet. Try refreshing the page or restarting your browser. If the problem continues, contact our support team for further assistance.",
      },
      {
        question: "What should I do if I encounter an error during payment?",
        answer:
          "Ensure that your payment details are correct and that your bank allows online payments. If the error persists, try again later or contact your bank and our support team for help.",
      },
      {
        question: "How do I track my payment status?",
        answer:
          "After completing the payment, you will receive a confirmation email. You can also log into your account to check status under 'Account Status'.",
      },
      {
        question: "I registered and when I log in, it does not show a button for payment.",
        answer:
          "1. Check if an account number exists on your profile. If not, contact support using the details below to assist you.",
      },
      {
        question: "Who do I contact for technical issues?",
        answer:
          "For technical issues, reach out to our customer support via email or phone. Our support details are available on the 'Contact Us' page.",
      },
    ],
    ZU: [
      {
        question: "Kuthatha isikhathi esingakanani ukuthumela i-OTP?",
        answer:
          "I-OTP (Inombolo Eyodwa Yesikhashana) imvamisa ithunyelwa ngemizuzwana embalwa. Nokho, kwezinye izikhathi ezingavamile, kungathatha kuze kufike emizuzwini emi-2. Uma ungatholi i-OTP, qinisekisa ukuthi inombolo yakho yeselula ilungile futhi hlola amafolda wezithunywa ezingafunwa noma ezivinjiwe.",
      },
      {
        question: "Yini okufanele ngiyenze uma ngingayitholi i-OTP?",
        answer:
          "Uma ungayitholi i-OTP, zama lezi zinyathelo ezilandelayo: (1) Qinisekisa ukuthi unokusabalala okuhle kwenethiwekhi. (2) Chofoza ku-‘Thumela I-OTP Kabusha’ ekhasini. (3) Uma inkinga iqhubeka, xhumana nethimba lethu lokusekela ukuze uthole usizo.",
      },
      {
        question: "Kungani ingakhombisi ekhasini lokukhokha?",
        answer:
          "Uma ikhasi lokukhokha lingalayishi, qinisekisa ukuthi uxhumano lwakho lwe-inthanethi luzinzile. Sula i-cache yesiphequluli sakho bese uzama futhi. Uma inkinga iqhubeka, zama ukusebenzisa isiphequluli noma idivayisi ehlukile bese uxhumana nethimba lokusekela uma kudingeka.",
      },
      {
        question: "Kungani ikhasi livimba noma lingasabeli?",
        answer:
          "Ikhasi elivimbayo lingadalwa idivayisi eshesha kancane noma inethiwekhi engazinzile. Zama ukuvuselela ikhasi noma uqale kabusha isiphequluli sakho. Uma inkinga iqhubeka, xhumana nethimba lethu lokusekela ukuze uthole usizo olunzulu.",
      },
      {
        question: "Yini okufanele ngiyenze uma ngihlangabezana nephutha ngesikhathi sokukhokha?",
        answer:
          "Qinisekisa ukuthi imininingwane yakho yokukhokha ilungile nokuthi ibhange lakho livumela izinkokhelo ze-inthanethi. Uma iphutha liqhubeka, zama futhi kamuva noma xhumana nebhange lakho kanye nethimba lethu lokusekela ukuze uthole usizo.",
      },
      {
        question: "Ngingayilandela kanjani isimo sokukhokha kwami?",
        answer:
          "Ngemuva kokuphothula inkokhelo, uzothola i-imeyili yokuqinisekisa. Ungangena futhi ku-akhawunti yakho ukuze uhlole isimo ngaphansi kwe-‘Isimo Se-akhawunti’.",
      },
      {
        question:
          "Ngibhalisile kodwa uma ngingena ngemvume, akukhombisi inkinobho yokukhokha.",
        answer:
          "1. Hlola ukuthi inombolo ye-akhawunti ikhona kuphrofayela yakho. Uma ingekho, xhumana nethimba lokusekela usebenzisa imininingwane engezansi ukuze bathole usizo.",
      },
      {
        question: "Ngibani engingaxhumana nabo ngezinkinga zobuchwepheshe?",
        answer:
          "Ngokwezinkinga zobuchwepheshe, xhumana nethimba lethu lokusekela amakhasimende nge-imeyili noma ngomakhalekhukhwini. Imininingwane yethu yokusekela iyatholakala ekhasini elithi ‘Xhumana Nathi’.",
      },
    ],
  };

  const videos = {
    EN: [
      {
        title: "How to Sign Up",
        src: "/signup-video.mp4",
      }, 
      {
        title: "How to Recharge",
        src: "/recharge-video.mp4",
      },
    ],
    ZU: [
      {
        title: "Indlela Yokubhalisa",
        src: "/zulu-signup.mp4",
      },
      {
        title: "Indlela Yokushajela Kabusha",
        src: "/zulu-recharge.mp4",
      },
    ],
  };

  useEffect(() => {
    setVideoContent(videos[language]);
  }, [language]);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="help_page" style={{ padding: "70px", backgroundColor: "#f8f9fa" }}>
      <h2 style={{ color: "#e2520f", textAlign: "center", fontWeight: "bold", marginBottom: "20px" }}>Help & Support</h2>
      <p style={{ textAlign: "center", color: "#666", marginBottom: "30px" }}>
        Find answers to your questions or explore our tutorials to get started.
      </p>

      {/* Language Selector */}
      <div style={{ textAlign: "right", marginBottom: "20px" }}>
        <label htmlFor="language-select" style={{ marginRight: "10px", fontWeight: "bold",color: "#0f174f" }}>
          Select Language:
        </label>
        <select
          id="language-select"
          value={language}
          onChange={(e) => setLanguage(e.target.value as "EN" | "ZU")}
          style={{ padding: "5px", borderRadius: "5px", border: "1px solid #ddd" }}
        >
          <option value="EN">English</option>
          <option value="ZU">Zulu</option>
        </select>
      </div>

      {/* Section 1: Video Tutorials */}
      <section className="video_grid" style={{ margin: "40px 0", border: "1px solid #ddd", borderRadius: "10px", padding: "20px" }}>
        <h4 style={{ color: "#E2520F", fontWeight: "bold"}}>
          VIDEO TUTORIALS
        </h4>
        <hr/>
        <div className="row">
          {videoContent.map((video, index) => (
            <div className="col-md-6 mb-3" key={index}> {/* Use Bootstrap's grid system */}
              <h3
                style={{
                  fontSize: "15px",
                  fontWeight: "bold",
                  color: "#e2520f",
                }}
              >
                {video.title}
              </h3>
              <video
                key={video.src}
                width="100%"
                height="auto"
                controls
                style={{ borderRadius: "5px" }}
              >
                <source src={video.src} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          ))}
        </div>
      </section>

      {/* Section 2: FAQs */}
      <section style={{ marginTop: "40px", border: "1px solid #ddd", borderRadius: "10px", padding: "20px" }}>
        <h4 style={{ color: "#E2520F", fontWeight: "bold"}}>
          Frequently Asked Questions
        </h4>
        <hr/>
        <p style={{ textAlign: "start", color: "#666", marginBottom: "30px" }}>
          Please contact us if you cannot find an answer to your question.
        </p>
        <div>
          {faqs[language].map((faq, index) => (
            <div key={index} style={{ marginBottom: "20px", borderBottom: "1px solid #ddd"}}>
              <div
                onClick={() => toggleFAQ(index)}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  fontWeight: "bold",
                  color: openIndex === index ? "#e2520f" : "#000",
                }}
              >
                <span style={{color: "#0f174f" }}>{faq.question}</span>
                <span style={{ color: "#e2520f" }}>
                  {openIndex === index ? "▼" : "▲"}
                </span>
              </div>
              {openIndex === index && (
                <div
                  style={{
                    padding: "10px 0",
                    color: "#666",
                  }}
                >
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
      <br/>
      <br/>
      <br/>
    </section>
    
  );
};

export default Help;

